import { Subscribe } from '@react-rxjs/core';
import {
  ContextType,
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import { DownloadButton } from '../DownloadButton';
import { Logo } from '../Logo';
import styles from './Shell.module.css';
import { FormattedMessage } from 'react-intl';
import MenuIcon from '../../assets/icons/menu.svg?react';
import { setSettingsSidebar } from '../SettingsSidebar/service';
import { SettingsSidebar } from '../SettingsSidebar';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Spacer } from '../Spacer';

export const ShellContext = createContext<{
  mini: [boolean, Dispatch<SetStateAction<boolean>>];
  content: [ReactNode, Dispatch<SetStateAction<ReactNode>>];
  title: [ReactNode, Dispatch<SetStateAction<ReactNode>>];
  hideMenu: [boolean, Dispatch<SetStateAction<boolean>>];
  rightContent: [ReactNode, Dispatch<SetStateAction<ReactNode>>];
}>({
  content: [null, () => {}],
  title: [null, () => {}],
  mini: [false, () => {}],
  hideMenu: [false, () => {}],
  rightContent: [null, () => {}],
});

interface HeaderProps {}

export const Header: FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const {
    mini: [mini],

    title: [title],
    rightContent: [rightContent],
  } = useContext(ShellContext);

  return (
    <>
      <header className={clsx(styles.header, mini && styles.mini)}>
        <div className={styles.logoRow}>
          <Logo className={styles.logo} onClick={() => navigate('/')} />
          {title && <div className={styles.title}>{title}</div>}
          <Spacer />
          <Subscribe fallback={null}>
            <DownloadButton className={styles.downloadBtn}>
              <FormattedMessage id="App.Download" />
            </DownloadButton>
          </Subscribe>
          <MenuIcon
            className={styles.menu}
            onClick={() => setSettingsSidebar(true)}
            data-testid="settings-menu"
          />
          {rightContent && <>{rightContent}</>}
        </div>
      </header>
      <SettingsSidebar />
    </>
  );
};

type SlottableKeys = keyof Pick<
  ContextType<typeof ShellContext>,
  'content' | 'rightContent' | 'title'
>;
export const HeaderSlot = ({
  slot,
  children,
}: {
  slot: SlottableKeys;
  children: ReactNode;
}) => {
  const [, setContent] = useContext(ShellContext)[slot];

  useEffect(() => {
    setContent(children);

    return () => {
      setContent(null);
    };
  }, [children, setContent]);

  return null;
};
